import React, { Children } from "react";
import Modal from "./Modal";
import Image from "../atoms/Image";
import LogoPlaceholder from "../../../assets/images/logo-placeholder.png";
import Routes from "../../constants/Routes";
import LinkHandler from "../atoms/LinkHandler";

interface PropsType {
    active: boolean;
    onClose: () => void;
    onLinkSelection: () => void;
    clubs: {
        ID: number;
        post_title?: string;
        acf: {
            logo?: { url?: string };
        };
    }[];
    activeClubId: number;
    setActiveClub: (clubId: string) => void;
    submitEventUrl?: string;
    submitGrantUrl?: string;
    resourcesUrl?: string;
    submitGovernanceUrl?: string;
    submitSponsorshipUrl?: string;
}

const ClubExecMenuModal = ({
    active,
    onClose,
    clubs,
    activeClubId,
    setActiveClub,
    submitEventUrl,
    submitGrantUrl,
    resourcesUrl,
    submitGovernanceUrl,
    submitSponsorshipUrl,
    onLinkSelection,
}: PropsType) => {
    const activeClub = clubs.find(({ ID }) => activeClubId === ID);
    const clubBaseUrl = `${Routes.manageClubs}/${activeClubId}`;

    const menuLinks: ({
        label: string;
        url: string;
        children?: ({ label: string; url: string } | null)[];
    } | null)[] = [
        {
            label: "Events",
            url: `${clubBaseUrl}/events`,
            children: [
                submitEventUrl
                    ? { label: "Apply for a new event", url: submitEventUrl }
                    : null,
                {
                    label: "View application status",
                    url: `${clubBaseUrl}/events/applications`,
                },
                { label: "Upcoming events", url: `${clubBaseUrl}/events` },
                { label: "Past events", url: `${clubBaseUrl}/events/past` },
            ],
        },
        {
            label: "Grants",
            url: `${clubBaseUrl}/grants`,
            children: [
                submitGrantUrl
                    ? { label: "Apply for a new grant", url: submitGrantUrl }
                    : null,
                {
                    label: "View application status",
                    url: `${clubBaseUrl}/grants/applications`,
                },
            ],
        },
        resourcesUrl
            ? {
                  label: "Resources",
                  url: resourcesUrl,
              }
            : null,
        {
            label: "Members",
            url: `${clubBaseUrl}/members`,
        },
        {
            label: "Governance",
            url: `${clubBaseUrl}/governance`,
            children: [
                submitGovernanceUrl
                    ? { label: "Submit form", url: submitGovernanceUrl }
                    : null,
                {
                    label: "View submitted forms",
                    url: `${clubBaseUrl}/governance`,
                },
            ],
        },
        {
            label: "Sponsorships",
            url: `${clubBaseUrl}/sponsorships`,
            children: [
                submitSponsorshipUrl
                    ? {
                          label: "Apply for a new sponsorship",
                          url: submitSponsorshipUrl,
                      }
                    : null,
                {
                    label: "View sponsorships",
                    url: `${clubBaseUrl}/sponsorships`,
                },
            ],
        },
    ];

    return (
        <Modal
            aside={true}
            active={active}
            className="club-exec-menu-modal"
            onClose={onClose}
            onOverlayClick={onClose}
        >
            <Modal.Header clear={true} />
            <Modal.Content>
                <div className="club-exec-menu-modal__container">
                    <h2 className="club-exec-menu-modal__title">
                        Manage your club
                    </h2>
                    {activeClubId ? (
                        <>
                            <div className="club-exec-menu-modal__select__wrapper">
                                <div className="club-exec-menu-modal__select__logo">
                                    {activeClub ? (
                                        <Image
                                            key={activeClub.ID}
                                            src={
                                                activeClub.acf.logo &&
                                                activeClub.acf.logo.url
                                                    ? activeClub.acf.logo.url
                                                    : LogoPlaceholder
                                            }
                                            alt="Club Logo"
                                            aspectRatio="1:1"
                                        />
                                    ) : null}
                                </div>
                                {clubs.length > 1 ? (
                                    <select
                                        className="club-exec-menu-modal__select"
                                        value={activeClubId}
                                        onChange={e =>
                                            setActiveClub(e.target.value)
                                        }
                                    >
                                        {clubs.map((club, key) => {
                                            return (
                                                <option
                                                    key={club.ID}
                                                    value={club.ID}
                                                >
                                                    {club.post_title}
                                                </option>
                                            );
                                        })}
                                    </select>
                                ) : (
                                    <div className="club-exec-menu-modal__club-text">
                                        {activeClub?.post_title ?? ""}
                                    </div>
                                )}
                                <div className="club-exec-menu-modal__select__select-indicator"></div>
                            </div>
                            <div className="club-exec-menu-modal__links">
                                <ul>
                                    {menuLinks.map(section =>
                                        section ? (
                                            <li
                                                key={`${section.label}-${section.url}`}
                                                className="club-exec-menu-modal__links__section"
                                            >
                                                <div>
                                                    <LinkHandler
                                                        to={section.url}
                                                    >
                                                        <h3
                                                            className="club-exec-menu-modal__links__section-label"
                                                            onClick={
                                                                onLinkSelection
                                                            }
                                                        >
                                                            {section.label}
                                                        </h3>
                                                    </LinkHandler>
                                                </div>
                                                <ul>
                                                    {section.children?.map(
                                                        child =>
                                                            child ? (
                                                                <LinkHandler
                                                                    key={`${child.label}-${child.url}`}
                                                                    to={
                                                                        child.url
                                                                    }
                                                                >
                                                                    <li
                                                                        className="club-exec-menu-modal__links__child-label"
                                                                        onClick={
                                                                            onLinkSelection
                                                                        }
                                                                    >
                                                                        {
                                                                            child.label
                                                                        }
                                                                    </li>
                                                                </LinkHandler>
                                                            ) : null
                                                    )}
                                                </ul>
                                            </li>
                                        ) : null
                                    )}
                                </ul>
                            </div>
                        </>
                    ) : null}
                </div>
            </Modal.Content>
        </Modal>
    );
};

export default ClubExecMenuModal;
